import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Button,
    Modal,
    Form,
    DatePicker,
    Select,
    Input,
    Tag,
    Row,
    Col,
    Statistic,
    Upload,
    message,
    Space,
    Tooltip,
    Progress,
    Menu,
    Typography,
    Popconfirm
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    CalendarOutlined,
    UploadOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    DashboardOutlined,
    FileTextOutlined,
    CheckOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
    loadLeaveApplications,
    loadLeaveBalances,
    createLeaveApplication,
    updateLeaveApplication,
    approveLeaveApplication,
    rejectLeaveApplication,
} from 'store/actions/AuthActions';
import { loadLeaveTypes } from 'store/slices/MasterSlice';
import useUrlFilters from 'hooks/useUrlFilters';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Sider, Content } = Layout;
const { Title, Text } = Typography;

// Applications pagination lives in the URL (prefixed, as this renders inside the employee detail page).
const URL_FILTERS = {
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const EmployeeLeaves = ({ employeeId, hideSider = false, activeSection: controlledSection, urlPrefix = 'lv_' }) => {
    const dispatch = useDispatch();
    const leaveTypes = useSelector(s => s.master?.leave_types) || [];
    const user = useSelector(s => s.auth.user);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState('apply'); // 'apply', 'view', 'edit'
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [form] = Form.useForm();
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [internalSection, setInternalSection] = useState('overview');
    const activeSection = controlledSection !== undefined ? controlledSection : internalSection;
    const setActiveSection = setInternalSection;
    const [appPagination, setAppPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [leaveBalances, setLeaveBalances] = useState([]);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [balanceYear] = useState(dayjs().year());
    const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: urlPrefix });
    const [rejectModal, setRejectModal] = useState({ open: false, leaveId: null, reason: '' });

    const fetchApplications = async (page = 1, pageSize = 10) => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const res = await loadLeaveApplications(page, null, pageSize, employeeId);
            if (res?.data) {
                const results = res.data.results || res.data;
                setLeaves(Array.isArray(results) ? results : []);
                setAppPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } finally { setLoading(false); }
    };

    const fetchBalances = async () => {
        if (!employeeId) return;
        setBalanceLoading(true);
        try {
            const res = await loadLeaveBalances(1, 500, balanceYear, employeeId);
            if (res?.data) {
                const results = res.data.results || res.data;
                setLeaveBalances(Array.isArray(results) ? results : []);
            }
        } finally { setBalanceLoading(false); }
    };

    useEffect(() => { dispatch(loadLeaveTypes()); }, [dispatch]);

    useEffect(() => {
        if (employeeId) {
            fetchBalances();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    useEffect(() => {
        if (employeeId) {
            fetchApplications(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, urlFilters]);

    const showModal = (type = 'apply', leave = null) => {
        setModalType(type);
        setSelectedLeave(leave);
        setIsModalVisible(true);

        if (leave && type === 'edit') {
            form.setFieldsValue({
                type: leave.leave_type,
                dateRange: [dayjs(leave.start_date), dayjs(leave.end_date)],
                reason: leave.reason
            });
        } else if (leave && type === 'view') {
            // View modal doesn't need form values
        } else {
            form.resetFields();
            setFileList([]);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('apply');
        setSelectedLeave(null);
        form.resetFields();
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        if (!employeeId) {
            message.error('Missing employee context');
            return;
        }
        setLoading(true);
        try {
            const data = {
                employee: employeeId,
                leave_type: values.type,
                start_date: values.dateRange[0].format('YYYY-MM-DD'),
                end_date: values.dateRange[1].format('YYYY-MM-DD'),
                reason: values.reason,
                leave_duration: 'full_day',
                priority: 'normal',
            };

            if (modalType === 'edit' && selectedLeave) {
                await updateLeaveApplication(selectedLeave.id, data);
                message.success('Leave application updated successfully!');
            } else {
                await createLeaveApplication(data);
                message.success('Leave application submitted successfully!');
            }

            handleCancel();
            await fetchApplications(appPagination.current, appPagination.pageSize);
            await fetchBalances();
        } catch (err) {
            const detail = err?.response?.data;
            if (detail && typeof detail === 'object') {
                const msgs = Object.values(detail).flat().join('; ');
                message.error(msgs || 'Failed to submit leave application');
            } else {
                message.error('Failed to submit leave application');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (leaveId) => {
        try {
            await updateLeaveApplication(leaveId, { status: 'withdrawn' });
            message.success('Leave application withdrawn successfully!');
            await fetchApplications(appPagination.current, appPagination.pageSize);
            await fetchBalances();
        } catch {
            message.error('Failed to withdraw leave application');
        }
    };

    const handleApprove = async (leaveId) => {
        try {
            await approveLeaveApplication(leaveId, '');
            message.success('Leave application approved');
            await fetchApplications(appPagination.current, appPagination.pageSize);
            await fetchBalances();
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to approve leave application';
            message.error(msg);
        }
    };

    const handleReject = async (leaveId, reason) => {
        try {
            await rejectLeaveApplication(leaveId, reason || '');
            message.success('Leave application rejected');
            await fetchApplications(appPagination.current, appPagination.pageSize);
            await fetchBalances();
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to reject leave application';
            message.error(msg);
        }
    };

    const handleFileChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'green';
            case 'pending': return 'orange';
            case 'rejected': return 'red';
            case 'cancelled':
            case 'withdrawn': return 'default';
            default: return 'default';
        }
    };

    const menuItems = [
        { key: 'overview', icon: <DashboardOutlined />, label: 'Overview' },
        { key: 'applications', icon: <FileTextOutlined />, label: 'Leave Applications' },
    ];

    const columns = [
        {
            title: 'Leave Type',
            dataIndex: 'leave_type_name',
            key: 'leave_type_name',
            render: (text) => <Tag color="blue">{text}</Tag>,
            width: 140
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (record) => {
                const days = Number(record.total_days || 0);
                return (
                    <div>
                        <div>{dayjs(record.start_date).format('DD MMM YYYY')} - {dayjs(record.end_date).format('DD MMM YYYY')}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{days} day{days !== 1 ? 's' : ''}</div>
                    </div>
                );
            },
            width: 200
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <div>
                    <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
                        {status}
                    </Tag>
                    {status === 'approved' && record.approved_by_name && (
                        <div style={{ fontSize: '12px', color: '#52c41a' }}>
                            By: {record.approved_by_name}
                        </div>
                    )}
                    {status === 'rejected' && record.rejection_reason && (
                        <Tooltip title={record.rejection_reason}>
                            <InfoCircleOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
                        </Tooltip>
                    )}
                </div>
            ),
            width: 140
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            width: 200
        },
        {
            title: 'Applied Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? dayjs(text).format('DD MMM YYYY') : '-',
            width: 130
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => showModal('view', record)}
                        />
                    </Tooltip>
                    {record.status === 'pending' && (
                        <>
                            <Popconfirm
                                title="Approve this leave application?"
                                onConfirm={() => handleApprove(record.id)}
                                okText="Approve"
                                cancelText="Cancel"
                            >
                                <Tooltip title="Approve">
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<CheckOutlined />}
                                    />
                                </Tooltip>
                            </Popconfirm>
                            <Tooltip title="Reject">
                                <Button
                                    size="small"
                                    danger
                                    icon={<CloseOutlined />}
                                    onClick={() => setRejectModal({ open: true, leaveId: record.id, reason: '' })}
                                />
                            </Tooltip>
                            <Tooltip title="Edit">
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => showModal('edit', record)}
                                />
                            </Tooltip>
                            <Popconfirm
                                title="Withdraw this leave application?"
                                onConfirm={() => handleDelete(record.id)}
                                okText="Withdraw"
                                cancelText="Cancel"
                            >
                                <Tooltip title="Withdraw">
                                    <Button size="small" icon={<DeleteOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
            width: 220,
            fixed: 'right'
        },
    ];

    const renderOverview = () => {
        return (
            <Row gutter={[16, 16]}>
                {leaveBalances.map(b => {
                    const allocated = Number(b.allocated_days || 0) + Number(b.carried_forward_days || 0);
                    const used = Number(b.used_days || 0);
                    const remaining = b.available_days != null
                        ? Number(b.available_days)
                        : Math.max(allocated - used, 0);
                    const pct = allocated > 0 ? Math.round((used / allocated) * 100) : 0;
                    return (
                        <Col xs={24} sm={12} lg={6} key={b.id}>
                            <Card loading={balanceLoading}>
                                <Statistic
                                    title={b.leave_type_name}
                                    value={remaining}
                                    suffix={`/ ${allocated}`}
                                    valueStyle={{ color: '#3f8600' }}
                                    prefix={<CalendarOutlined />}
                                />
                                <Progress
                                    percent={pct}
                                    size="small"
                                    style={{ marginTop: '8px' }}
                                />
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                                    Used: {used} days
                                </div>
                            </Card>
                        </Col>
                    );
                })}
                {!balanceLoading && leaveBalances.length === 0 && (
                    <Col span={24}>
                        <Text type="secondary">No leave balances configured for this employee.</Text>
                    </Col>
                )}
            </Row>
        );
    };

    const renderApplications = () => {
        return (
            <Card
                title="Leave Applications"
                extra={
                    <Tooltip title="Apply for Leave">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showModal('apply')}
                            disabled={!employeeId}
                        >
                            Apply for Leave
                        </Button>
                    </Tooltip>
                }
            >
                <Table
                    columns={columns}
                    dataSource={leaves}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 800 }}
                    pagination={{
                        current: appPagination.current,
                        pageSize: appPagination.pageSize,
                        total: appPagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        // A new page size starts again from page 1.
                        onChange: (page, pageSize) => setUrlFilters({ page: pageSize !== appPagination.pageSize ? 1 : page, pageSize }),
                    }}
                />
            </Card>
        );
    };

    const getSectionTitle = () => {
        const titles = {
            'overview': 'Leave Overview',
            'applications': 'Leave Applications'
        };
        return titles[activeSection] || 'Leave Information';
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'overview':
                return renderOverview();
            case 'applications':
                return renderApplications();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            <Card
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    {!hideSider && (
                        <Sider
                            width={240}
                            style={{
                                background: '#fff',
                                borderRight: '1px solid #f0f0f0',
                                overflow: 'auto'
                            }}
                        >
                            <style>
                                {`
                                    .leaves-sidebar-menu .ant-menu-item-selected {
                                        background-color: #1890ff !important;
                                        color: #fff !important;
                                    }
                                    .leaves-sidebar-menu .ant-menu-item-selected .anticon {
                                        color: #fff !important;
                                    }
                                    .leaves-sidebar-menu .ant-menu-item-selected::after {
                                        border-right-color: #1890ff !important;
                                    }
                                `}
                            </style>
                            <Menu
                                mode="inline"
                                selectedKeys={[activeSection]}
                                onClick={({ key }) => setActiveSection(key)}
                                style={{ borderRight: 0 }}
                                items={menuItems}
                                className="leaves-sidebar-menu"
                            />
                        </Sider>
                    )}

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <Title level={5} style={{ marginBottom: 24 }}>
                            {getSectionTitle()}
                        </Title>
                        {renderContent()}
                    </Content>
                </Layout>
            </Card>

            {/* Modal for Apply/Edit/View Leave */}
            <Modal
                title={
                    modalType === 'view'
                        ? 'Leave Application Details'
                        : modalType === 'edit'
                            ? 'Edit Leave Application'
                            : 'Apply for Leave'
                }
                open={isModalVisible}
                onCancel={handleCancel}
                footer={modalType === 'view' ? [
                    <Button key="close" onClick={handleCancel}>
                        Close
                    </Button>
                ] : null}
                width={600}
            >
                {modalType === 'view' && selectedLeave ? (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div><strong>Leave Type:</strong></div>
                                <Tag color="blue" style={{ marginTop: 4 }}>{selectedLeave.leave_type_name}</Tag>
                            </Col>
                            <Col span={12}>
                                <div><strong>Status:</strong></div>
                                <Tag color={getStatusColor(selectedLeave.status)} style={{ marginTop: 4, textTransform: 'capitalize' }}>
                                    {selectedLeave.status}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <div><strong>Start Date:</strong></div>
                                <div>{dayjs(selectedLeave.start_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>End Date:</strong></div>
                                <div>{dayjs(selectedLeave.end_date).format('DD MMM YYYY')}</div>
                            </Col>
                            <Col span={12}>
                                <div><strong>Duration:</strong></div>
                                <div>
                                    {Number(selectedLeave.total_days || 0)} day{Number(selectedLeave.total_days) !== 1 ? 's' : ''}
                                    {selectedLeave.leave_duration && selectedLeave.leave_duration !== 'full_day' && (
                                        <span> — {String(selectedLeave.leave_duration).replace(/_/g, ' ')}</span>
                                    )}
                                </div>
                            </Col>
                            <Col span={12}>
                                <div><strong>Applied Date:</strong></div>
                                <div>{selectedLeave.created_at ? dayjs(selectedLeave.created_at).format('DD MMM YYYY') : '-'}</div>
                            </Col>
                            {selectedLeave.applied_by_name && (
                                <Col span={12}>
                                    <div><strong>Applied By:</strong></div>
                                    <div>{selectedLeave.applied_by_name}</div>
                                </Col>
                            )}
                            {selectedLeave.priority && (
                                <Col span={12}>
                                    <div><strong>Priority:</strong></div>
                                    <Tag style={{ marginTop: 4, textTransform: 'capitalize' }}>{selectedLeave.priority}</Tag>
                                </Col>
                            )}
                            {selectedLeave.approved_by_name && (
                                <>
                                    <Col span={12}>
                                        <div><strong>Approved By:</strong></div>
                                        <div>{selectedLeave.approved_by_name}</div>
                                    </Col>
                                    <Col span={12}>
                                        <div><strong>Approved Date:</strong></div>
                                        <div>{selectedLeave.approved_at ? dayjs(selectedLeave.approved_at).format('DD MMM YYYY') : '-'}</div>
                                    </Col>
                                </>
                            )}
                            {selectedLeave.rejection_reason && (
                                <Col span={24}>
                                    <div><strong>Rejection Reason:</strong></div>
                                    <div style={{ color: '#ff4d4f' }}>{selectedLeave.rejection_reason}</div>
                                </Col>
                            )}
                            <Col span={24}>
                                <div><strong>Reason:</strong></div>
                                <div style={{ marginTop: 4, padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                                    {selectedLeave.reason}
                                </div>
                            </Col>
                            {selectedLeave.supporting_document && (
                                <Col span={24}>
                                    <div><strong>Supporting Document:</strong></div>
                                    <div style={{ marginTop: 4 }}>
                                        <a href={selectedLeave.supporting_document} target="_blank" rel="noreferrer">
                                            View document
                                        </a>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            label="Leave Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select leave type!' }]}
                        >
                            <Select placeholder="Select leave type" size="large">
                                {leaveTypes.map(lt => (
                                    <Option key={lt.id} value={lt.id}>{lt.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Date Range"
                            name="dateRange"
                            rules={[{ required: true, message: 'Please select date range!' }]}
                        >
                            <RangePicker
                                style={{ width: '100%' }}
                                size="large"
                                disabledDate={(current) => {
                                    if (user?.is_staff || user?.is_superuser) return false;
                                    return current && current < dayjs().startOf('day');
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Reason"
                            name="reason"
                            rules={[
                                { required: true, message: 'Please provide a reason!' },
                                { min: 10, message: 'Reason must be at least 10 characters!' }
                            ]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Please provide the reason for your leave application..."
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>

                        <Form.Item
                            label="Supporting Document (Optional)"
                            name="document"
                        >
                            <Upload
                                fileList={fileList}
                                onChange={handleFileChange}
                                beforeUpload={() => false}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} size="large">
                                    Upload Document
                                </Button>
                            </Upload>
                            <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max: 5MB)
                            </div>
                        </Form.Item>

                        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                            <Space>
                                <Button onClick={handleCancel} size="large">
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                >
                                    {modalType === 'edit' ? 'Update Application' : 'Submit Application'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <Modal
                title="Reject Leave Application"
                open={rejectModal.open}
                onCancel={() => setRejectModal({ open: false, leaveId: null, reason: '' })}
                onOk={async () => {
                    const { leaveId, reason } = rejectModal;
                    setRejectModal({ open: false, leaveId: null, reason: '' });
                    await handleReject(leaveId, reason);
                }}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <div style={{ marginBottom: 8 }}>Reason for rejection (optional):</div>
                <TextArea
                    rows={4}
                    value={rejectModal.reason}
                    onChange={(e) => setRejectModal(s => ({ ...s, reason: e.target.value }))}
                    placeholder="Explain why this application is being rejected..."
                    maxLength={500}
                    showCount
                />
            </Modal>
        </div>
    );
};

export default EmployeeLeaves;
